import { useState } from "react";
import { Pressable, ActivityIndicator, Platform } from "react-native";
import styled from "styled-components/native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { httpsCallable, getFunctions } from "firebase/functions";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import AlertSheet from "@/components/AlertSheet";
import { useAlert } from "@/hooks/useAlert";

const functions = getFunctions(undefined, "europe-west2");

const Screen = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
`;

const Card = styled.View`
  margin: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 32px 24px;
  align-items: center;
`;

const AmountText = styled.Text`
  font-size: 40px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -1px;
`;

const Description = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  text-align: center;
  line-height: 22px;
  margin-top: 8px;
  margin-bottom: 28px;
`;

const PayBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  width: 100%;
`;

const PayBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const SecureText = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-top: 12px;
`;

const ErrorText = styled.Text`
  font-size: 14px;
  color: ${colors.danger};
  font-weight: ${font.bold};
  text-align: center;
  margin-top: 12px;
`;

export default function PayScreen() {
  const params = useLocalSearchParams<{
    amount: string;
    description: string;
    matchId?: string;
    feeId?: string;
    feeType?: string;
  }>();
  const router = useRouter();
  const { user } = useAuth();
  const { club } = useClub();
  const { alertProps, alert } = useAlert();

  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const amountPence = parseInt(params.amount || "0", 10);
  const amountPounds = (amountPence / 100).toFixed(2);

  const handlePay = async () => {
    if (!club || !user) return;
    setPaying(true);
    setError("");

    try {
      if (Platform.OS === "web") {
        // Web: Redirect to Stripe Checkout
        const createCheckout = httpsCallable(functions, "createCheckoutSession");
        const result = await createCheckout({
          amount: amountPence,
          description: params.description || "Crease payment",
          clubId: club.id,
          matchId: params.matchId,
          feeId: params.feeId,
          feeType: params.feeType || "match_fee",
          successUrl: window.location.origin + "/(app)/(tabs)?paymentSuccess=true",
          cancelUrl: window.location.href,
        });
        const { url } = result.data as { url: string };
        window.location.href = url;
      } else {
        // Native: Use Payment Sheet
        const { showPaymentSheet } = require("@/lib/stripe-native");

        const createIntent = httpsCallable(functions, "createPaymentIntent");
        const result = await createIntent({
          amount: amountPence,
          description: params.description || "Crease payment",
          clubId: club.id,
          matchId: params.matchId,
          feeId: params.feeId,
          feeType: params.feeType || "match_fee",
        });
        const { clientSecret, ephemeralKey, customerId, paymentIntentId } = result.data as any;

        const payResult = await showPaymentSheet({
          clientSecret,
          customerId,
          ephemeralKey,
          email: user.email || "",
        });

        if (payResult.error) {
          setError(payResult.error);
          setPaying(false);
          return;
        }

        if (!payResult.success) {
          setPaying(false);
          return; // cancelled
        }

        // Confirm in Firestore
        const confirmFn = httpsCallable(functions, "confirmPayment");
        await confirmFn({
          paymentIntentId,
          clubId: club.id,
          matchId: params.matchId,
          feeId: params.feeId,
        });

        await alert({
          title: "Payment successful",
          emoji: "✅",
          message: `£${amountPounds} paid for ${params.description}`,
        });
        router.back();
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setPaying(false);
    }
  };

  return (
    <Screen>
      <ContentWidth>
        <Card>
          <AmountText>£{amountPounds}</AmountText>
          <Description>{params.description || "Match fee"}</Description>
          <Pressable
            onPress={handlePay}
            disabled={paying}
            style={({ pressed }) => ({ width: "100%" as any, opacity: pressed ? 0.9 : 1 })}
          >
            <PayBtn>
              {paying ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <PayBtnText>Pay £{amountPounds}</PayBtnText>
              )}
            </PayBtn>
          </Pressable>
          <SecureText>🔒 Powered by Stripe · Secure payment</SecureText>
          {error ? <ErrorText>{error}</ErrorText> : null}
        </Card>
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </Screen>
  );
}
